/**
 * Tolerant readers for the agent's `JSONString` payloads, and the option/date
 * helpers the form fields share.
 *
 * Codegen types every `JSONString` as `string`, so everything past `JSON.parse` is
 * `unknown` until something checks it. The rule throughout: what we cannot read
 * becomes "nothing", never an exception — a version skew must not blank the chat.
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Not extended anywhere else in the app: this is the only place that needs strict
// `YYYY-MM-DD` parsing (see `isValidDate` below), so it's wired locally rather than
// depending on an app's root `_layout.tsx` extending it first.
dayjs.extend(customParseFormat);

export type SelectOption = {
  value: string;
  label: string;
  description: string;
  /** A thumbnail. What makes a product list recognizable instead of a wall of ids. */
  imageUrl: string;
  /** A short right-aligned annotation: a price, a platform. */
  badge: string;
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** An object, whether it arrived parsed or still as a JSON string; null otherwise. */
export const asRecord = (raw: unknown): Record<string, unknown> | null => {
  const source = typeof raw === "string" ? safeParse(raw) : raw;
  return isRecord(source) ? source : null;
};

export const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

export const asOption = (raw: unknown): SelectOption | null => {
  if (!isRecord(raw)) return null;
  const value = asString(raw.value);
  if (!value) return null;
  return {
    value,
    label: asString(raw.label, value),
    description: asString(raw.description),
    imageUrl: asString(raw.imageUrl),
    badge: asString(raw.badge),
  };
};

export const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Is `value` a real, strictly `YYYY-MM-DD` calendar day? `2026-02-31` matches the
 * shape but is not a day — `customParseFormat`'s strict mode is what catches it,
 * where a plain `dayjs(value)` would silently roll it over into March.
 */
export const isValidDate = (value: string): boolean =>
  dayjs(value, "YYYY-MM-DD", true).isValid();

/**
 * "4 de marzo de 2026" — never "2026-03-04", which half the world reads backwards.
 *
 * `dayjs(value)` (unlike `new Date(value)`) parses a bare `YYYY-MM-DD` string as
 * local midnight rather than UTC midnight, so there is no timezone round-trip to
 * get wrong here.
 */
export const readableDate = (value: string): string => {
  if (!isValidDate(value)) return value;
  return dayjs(value).locale("es").format("D [de] MMMM [de] YYYY");
};
